import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import React, { PropsWithChildren, createContext, use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconFiltersDisable from '~/icons/icon-filters-disable.svg?react';
import IconFiltersEnable from '~/icons/icon-filters-tune.svg?react';
import { useDirtyState } from '~/state/tableFilters';
import ClearFiltersButton from './ClearFiltersButton';
import styles from './CollapsibleFilters.module.scss';
import Button from '../buttons/NewButton';
import IconButton from '../buttons/NewIconButton';
import { ActionBadge } from '../data-display/ActionBadge/ActionBadge';
import { VStack } from '../layout/Stack';
import Text from '../typographies/Text';

const CollapsibleFiltersContext = createContext<{
  isExpanded: boolean;
  setExpanded: (value: boolean) => void;
  dirtyState: object;
  onReset?: () => void;
  hasAdditionalFilters: boolean;
  setHasAdditionalFilters: (value: boolean) => void;
  showHideLabels: boolean;
}>({
  isExpanded: false,
  setExpanded: noop,
  dirtyState: {},
  hasAdditionalFilters: false,
  setHasAdditionalFilters: noop,
  showHideLabels: false,
});

interface CollapsibleFiltersProps<T extends object = object> {
  className?: string;
  dirtyState?: Partial<T>;
  ignoreKeys?: (keyof T)[] | undefined;
  onReset?: () => void;
  showClearButton?: boolean;
  showHideLabels?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (value: boolean) => void;
}

interface MoreFiltersButtonProps {
  counter: number;
  variant?: 'regular' | 'compact';
}

const MoreFiltersButton: React.FC<MoreFiltersButtonProps> = ({ counter, variant }) => {
  const { t } = useTranslation('inputs', { keyPrefix: 'collapsible_filters' });
  const { isExpanded, setExpanded, showHideLabels } = use(CollapsibleFiltersContext);

  if (variant === 'compact') {
    return (
      <div className={styles.moreFiltersButtonWrapper}>
        <IconButton
          className={styles.moreFiltersButton}
          Icon={isExpanded ? IconFiltersDisable : IconFiltersEnable}
          variant={isExpanded ? 'outlined' : undefined}
          onClick={() => setExpanded(!isExpanded)}
        />
        {!isExpanded && counter > 0 && (
          <ActionBadge className={styles.moreFiltersCounterBadge}>
            <Text variant="caption" weight="semibold">
              {counter}
            </Text>
          </ActionBadge>
        )}
      </div>
    );
  }

  const label = showHideLabels
    ? t(isExpanded ? 'hide_filters' : 'show_filters', { count: counter })
    : t('more_filters', { count: counter });

  return (
    <Button
      className={styles.moreFiltersButton}
      variant="ghost"
      Icon={IconFiltersEnable}
      onClick={() => setExpanded(!isExpanded)}
    >
      {label}
    </Button>
  );
};

export const MoreFiltersButtonWithDynamicLabels: React.FC = () => {
  const { t } = useTranslation('inputs', { keyPrefix: 'collapsible_filters' });
  const { isExpanded, setExpanded, dirtyState } = use(CollapsibleFiltersContext);
  const counter = Object.keys(dirtyState).length;

  return (
    <Button
      className={styles.moreFiltersButton}
      variant="ghost"
      Icon={IconFiltersEnable}
      onClick={() => setExpanded(!isExpanded)}
    >
      {t(isExpanded ? 'hide_filters' : 'show_filters', { count: counter })}
    </Button>
  );
};

interface PrimaryFiltersProps extends PropsWithChildren {
  className?: string;
  variant?: 'regular' | 'compact';
  showClearButton?: boolean;
  showMoreFiltersButton?: boolean;
  customFilterActions?: React.ReactNode;
}

export const PrimaryFilters = (props: PrimaryFiltersProps) => {
  const {
    children,
    variant = 'regular',
    showClearButton = true,
    showMoreFiltersButton = true,
    customFilterActions,
  } = props;
  const { dirtyState, onReset, hasAdditionalFilters } = use(CollapsibleFiltersContext);

  const counter = Object.keys(dirtyState).length;
  const isDirty = isEmpty(dirtyState) === false;
  const shouldShowClearBtn = showClearButton && isDirty;

  return (
    <div className={clsx(styles.filtersBox, styles.primaryFilters, props.className)}>
      {children}
      {showMoreFiltersButton && hasAdditionalFilters && <MoreFiltersButton counter={counter} variant={variant} />}
      {customFilterActions}
      {shouldShowClearBtn && <ClearFiltersButton className={styles.clearFiltersButton} onReset={onReset} inline />}
    </div>
  );
};

export const AdditionalFilters: React.FC<PropsWithChildren<{ className?: string }>> = (props) => {
  const { children } = props;
  const { isExpanded, setHasAdditionalFilters } = use(CollapsibleFiltersContext);

  useEffect(() => {
    setHasAdditionalFilters(true);
    return () => setHasAdditionalFilters(false);
  }, [setHasAdditionalFilters]);

  return (
    <div className={styles.secondaryFilters} data-expanded={isExpanded}>
      <div className={clsx(styles.filtersBox, styles.secondaryFiltersContent, props.className)}>{children}</div>
    </div>
  );
};

interface CollapsibleFiltersInnerProps extends PropsWithChildren {
  className?: string;
  dirtyState: object;
  onReset?: () => void;
  showClearButton?: boolean;
  showHideLabels?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (value: boolean) => void;
}

const CollapsibleFiltersInner = ({
  children,
  className,
  dirtyState,
  onReset,
  showClearButton = false,
  showHideLabels = false,
  isExpanded: isExpandedProp,
  onExpandedChange,
}: CollapsibleFiltersInnerProps) => {
  const [isExpandedInternal, setExpandedInternal] = useState(false);
  const isControlled = isExpandedProp !== undefined;
  const isExpanded = isControlled ? isExpandedProp : isExpandedInternal;
  const setExpanded = (value: boolean) => {
    if (!isControlled) setExpandedInternal(value);
    onExpandedChange?.(value);
  };
  const [hasAdditionalFilters, setHasAdditionalFilters] = useState(false);

  return (
    <VStack gap={1} className={clsx(styles.root, className)}>
      <CollapsibleFiltersContext.Provider
        value={{
          isExpanded,
          setExpanded,
          dirtyState,
          onReset,
          hasAdditionalFilters,
          setHasAdditionalFilters,
          showHideLabels,
        }}
      >
        <>
          {children}
          {showClearButton && !isEmpty(dirtyState) && (
            <ClearFiltersButton className={styles.clearFiltersParentButton} inline />
          )}
        </>
      </CollapsibleFiltersContext.Provider>
    </VStack>
  );
};

const CollapsibleFiltersWithHook = <Filter extends object = object>({
  ignoreKeys,
  ...props
}: PropsWithChildren<Omit<CollapsibleFiltersProps<Filter>, 'dirtyState'>>) => {
  const dirtyState = useDirtyState(ignoreKeys);
  return <CollapsibleFiltersInner {...props} dirtyState={dirtyState} />;
};

const CollapsibleFilters = <Filter extends object = object>({
  dirtyState,
  ignoreKeys,
  ...props
}: PropsWithChildren<CollapsibleFiltersProps<Filter>>) => {
  if (dirtyState !== undefined) {
    return <CollapsibleFiltersInner {...props} dirtyState={dirtyState} />;
  }
  return <CollapsibleFiltersWithHook<Filter> {...props} ignoreKeys={ignoreKeys} />;
};

export default CollapsibleFilters;
